import React, { useState, useRef } from 'react'
import { Flex, FlexCenter } from '../Flex'
import { InputModal } from '../InputModal'
import { Close } from '../Icon'
import { H4 } from '../Heading'
import { businessContactSchema } from '../../utils/schema'
import { BusinessContactModalProps } from '../../types'
import type { BusinessContactData } from '../../types/propsType_v1'
import { BusinessContactForm } from './business-contact-form'
import { contactsServices } from '../../../services'
import { StatusLabel } from '../StatusLabel'
import '../ContactUsModal/contact-us.scss'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../../global-state/banner-slice'
import ReCAPTCHA from 'react-google-recaptcha'
import { trackGaLeadFormSubmit } from '../../analytics/google-analytics'

const BusinessContactModal = ({
  active,
  handleOnClose,
  initialEmail = '',
}: BusinessContactModalProps) => {
  const reRef = useRef<ReCAPTCHA>(null)
  const dispatch = useDispatch()
  const [apiResponse, setApiResponse] = useState<{
    type: 'success' | 'error'
    message: string
  }>({
    type: 'success',
    message: '',
  })

  const [initialValues, setInitialValues] = useState<BusinessContactData>({
    firstName: '',
    lastName: '',
    company: '',
    email: initialEmail,
    message: '',
  })
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (active && initialEmail) {
      setInitialValues((prev) => ({ ...prev, email: initialEmail }))
    }
  }, [active, initialEmail])

  const handleSubmit = async (values: BusinessContactData) => {
    reRef.current?.reset()

    const data = await contactsServices.submitBusinessContactForm(values, {
      'g-recaptcha-response': captchaToken ? captchaToken : '',
    })

    if (!!data && data.status === 200) {
      setApiResponse({
        type: 'success',
        message: data.data.message,
      })
      trackGaLeadFormSubmit('business_contact')

      dispatch(
        showBanner({
          text: 'Your inquiry has been submitted successfully. We will get back to you soon.',
          variant: 'success-banner',
        }),
      )
      handleOnClose()
    } else {
      const message = data.data ? data.data.message : 'Error recording your data'
      setApiResponse({
        type: 'error',
        message: message,
      })

      dispatch(
        showBanner({
          text: 'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <div>
      {active ? (
        <InputModal>
          <FlexCenter variant='column' classes={['contact-us-modal-container']}>
            <div
              className='contact-us-card-container p-24 contact-us-form'
              data-testid='business-contact-modal'
            >
              <Flex variant='space-between' classes={['mb-32']}>
                <H4 text='Get in Touch' />
                <Close type='default-light' onClick={handleOnClose} />
              </Flex>

              <BusinessContactForm
                validationSchema={businessContactSchema}
                handleSubmit={handleSubmit}
                initialValues={initialValues}
                reRef={reRef}
                captchaToken={captchaToken}
                setCaptchaToken={setCaptchaToken}
              />
              {apiResponse.message.length > 0 && (
                <FlexCenter>
                  <StatusLabel
                    type={apiResponse.type}
                    text={apiResponse.message}
                    variant={'bold'}
                    dataTestId='business-contact-modal-api-response'
                  />
                </FlexCenter>
              )}
            </div>
          </FlexCenter>
        </InputModal>
      ) : null}
    </div>
  )
}
export { BusinessContactModal }
